import { useCallback, useRef, useState, type ChangeEvent, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { SendIcon, PaperclipIcon, ImageIcon, MicIcon } from '@utils/icons';
import type { CommsMember } from '@apis/comms';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import type { MessageSegment } from '@interfaces/ICommsApi';
import { TOO_SHORT_NOTICE_MS, useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import { useComposerAttachments, voiceAttachment, type IPendingAttachment } from '../../hooks/useComposerAttachments';
import { useMessageEditor } from '../../hooks/useMessageEditor';
import { MentionPicker } from './MentionPicker/MentionPicker';
import { PendingAttachments } from './PendingAttachment/PendingAttachments';
import { VoicePreviewBar } from './PendingAttachment/VoicePreviewBar/VoicePreviewBar';
import { RecordingBar } from './RecordingBar/RecordingBar';
import styles from './Composer.module.css';
import { isImeComposing } from '@utils/ime';

interface IToolButtonProps {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}

/** An icon tool with its hint; the span keeps the hint working while the button is disabled. */
function ToolButton({ label, disabled, onClick, children }: IToolButtonProps) {
  return (
    <Tooltip content={label}>
      <span className={styles.toolHint}>
        <button type="button" className={styles.iconBtn} disabled={disabled} onClick={onClick} aria-label={label}>
          {children}
        </button>
      </span>
    </Tooltip>
  );
}

interface IComposerProps {
  channelName: string; // Display label — channel name, or the DM peer's name
  isDm: boolean;
  members: CommsMember[];
  onSend: (segments: MessageSegment[], attachments: IPendingAttachment[]) => void;
}

export function Composer({ channelName, isDm, members, onSend }: IComposerProps) {
  const { t } = useTranslation();
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const recorder = useVoiceRecorder();
  const att = useComposerAttachments();
  const editor = useMessageEditor(editorRef);

  const [autoStopped, setAutoStopped] = useState(false);

  // A voice note is sent on its own, so it takes over the editor row; other
  // attachments (images/files) stay as chips above it.
  const voiceItem = att.pending.find((p) => p.kind === 'voice');
  const otherPending = att.pending.filter((p) => p.kind !== 'voice');

  // Gather text + pending attachments, guard, send, then reset both.
  const submit = useCallback(() => {
    const segments = editor.serialize();
    const hasText = segments.some((s) => (s.kind === 'text' ? s.text.trim().length > 0 : true));
    if (!hasText && !att.hasPending) return;
    if (editor.overLimit) return;
    if (!att.allReady) return; // Voice blob still being fetched from local recorder URL
    onSend(segments, att.pending);
    editor.clear();
    att.clear();
    setAutoStopped(false);
    recorder.dismissTooShort();
  }, [editor, att, onSend, recorder]);

  // Send while recording finishes the note and sends it straight away.
  async function sendRecording() {
    const rec = await recorder.stop();
    if (!rec) return;
    URL.revokeObjectURL(rec.url);
    onSend([], [...att.pending, voiceAttachment(rec.blob, rec.durationMs)]);
    att.clear();
  }

  function handleFormSubmit(e: { preventDefault(): void }) {
    e.preventDefault();
    if (recorder.recording) void sendRecording();
    else submit();
  }

  // The editor hook handles the `@` trigger; Enter→submit is orchestration.
  function handleEditorKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    editor.onKeyDown(e);
    if (e.key === 'Enter' && !e.shiftKey && !editor.mentionOpen && !isImeComposing(e)) {
      e.preventDefault();
      submit();
    }
  }

  function addFileAttachments(e: ChangeEvent<HTMLInputElement>, kind: 'image' | 'file') {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    att.addFiles(files, kind);
  }

  async function handleStartRecording() {
    if (recorder.recording) return;
    setAutoStopped(false);
    await recorder.start((rec) => {
      att.addVoice(rec);
      setAutoStopped(true);
    });
  }

  const canSend = recorder.recording || ((!editor.isEmpty || att.hasPending) && att.allReady && !editor.overLimit);

  return (
    <form className={styles.root} onSubmit={handleFormSubmit}>
      <PendingAttachments items={otherPending} onRemove={att.remove} />

      <div className={styles.inputRow}>
        {/* Attachment Buttons */}
        <div className={styles.tools}>
          <ToolButton
            label={t('comms.composer.attachFile')}
            disabled={recorder.recording || !!voiceItem}
            onClick={() => fileInputRef.current?.click()}
          >
            <PaperclipIcon size={18} />
          </ToolButton>
          <ToolButton
            label={t('comms.composer.attachImage')}
            disabled={recorder.recording || !!voiceItem}
            onClick={() => imageInputRef.current?.click()}
          >
            <ImageIcon size={18} />
          </ToolButton>
          <ToolButton
            label={t(recorder.supported ? 'comms.composer.recordVoice' : 'comms.composer.voiceUnsupported')}
            disabled={!recorder.supported || recorder.recording || !!voiceItem}
            onClick={handleStartRecording}
          >
            <MicIcon size={18} />
          </ToolButton>
        </div>

        {recorder.recording ? (
          <RecordingBar elapsedMs={recorder.elapsedMs} getLevel={recorder.getLevel} onCancel={recorder.cancel} />
        ) : voiceItem && voiceItem.previewUrl ? (
          <div className={styles.voicePreviewWrap}>
            {autoStopped && (
              <span className={styles.maxLengthLabel}>
                {t('comms.composer.maxLengthReached')}
              </span>
            )}
            <VoicePreviewBar
              url={voiceItem.previewUrl}
              durationMs={voiceItem.durationMs ?? 0}
              onRemove={() => { att.remove(voiceItem.localId); setAutoStopped(false); }}
            />
          </div>
        ) : (
          <div className={styles.editorWrap}>
            <ScrollArea className={styles.editor}>
              <div
                ref={editorRef}
                className={`${styles.editable} ${editor.isEmpty ? styles.editorEmpty : ''}`}
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                aria-label={isDm
                    ? t('comms.composer.placeholderDm', { name: channelName })
                    : t('comms.composer.placeholder', { channel: channelName })}
                data-placeholder={isDm
                    ? t('comms.composer.placeholderDm', { name: channelName })
                    : t('comms.composer.placeholder', { channel: channelName })}
                onInput={editor.onInput}
                onKeyDown={handleEditorKeyDown}
                onBeforeInput={editor.onBeforeInput}
                onPaste={editor.onPaste}
              />
            </ScrollArea>
            {editor.mentionOpen && (
              <MentionPicker
                members={members}
                onPick={editor.insertMention}
                onClose={() => { editor.closeMention(); editor.focus(); }}
              />
            )}
          </div>
        )}

        {editor.nearLimit && (
          <span
            className={`${styles.charCount} ${editor.overLimit ? styles.charCountOver : ''}`}
            aria-live="polite"
          >
            {editor.remaining}
          </span>
        )}

        <button
          type="submit"
          className={`${styles.sendBtn} ${canSend ? styles.sendBtnActive : ''}`}
          disabled={!canSend}
          aria-label={t('comms.composer.send')}
        >
          <SendIcon size={16} />
        </button>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className={styles.hiddenInput}
          onChange={(e) => addFileAttachments(e, 'file')}
        />
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          className={styles.hiddenInput}
          onChange={(e) => addFileAttachments(e, 'image')}
        />
      </div>

      {recorder.tooShort && (
        <div
          className={`${styles.noticeRow} ${styles.noticeTimed}`}
          role="status"
          style={{ '--notice-ms': `${TOO_SHORT_NOTICE_MS}ms` } as CSSProperties}
        >
          {t('comms.composer.tooShort')}
        </div>
      )}
      {recorder.error && (
        <div className={styles.errorRow} role="alert">
          {t('comms.composer.recordError')}
        </div>
      )}
    </form>
  );
}
